import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Customer,
  InternalLineItem,
  MapperRow,
  MapperView,
  MappingImportResult,
  PriceListOption,
} from '@tcg/shared';
import { ApiClientError, adminApi } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui';
import { IconMapping } from '../../components/icons';
import { Combobox } from '../../components/Combobox';

export function PriceListMapping() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [mapper, setMapper] = useState<MapperView | null>(null);
  const [internalItems, setInternalItems] = useState<InternalLineItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newTaxable, setNewTaxable] = useState<'null' | 'true' | 'false'>('null');
  const [newCustId, setNewCustId] = useState('');
  const [newCustName, setNewCustName] = useState('');
  const [refreshWoId, setRefreshWoId] = useState('');
  // Free-text filter for the Facil-IT mapping table — search by Facil-IT name/code
  // or by the Corrigo item it's currently mapped to.
  const [rowFilter, setRowFilter] = useState('');

  const run = useCallback(async (fn: () => Promise<void>, ok?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    setCustomers(await adminApi.listCustomers());
  }, []);

  const loadMapper = useCallback(async (customerId: string) => {
    setMapper(customerId ? await adminApi.getMapper(customerId) : null);
  }, []);

  const loadInternalItems = useCallback(async () => {
    setInternalItems(await adminApi.listInternalItems());
  }, []);

  useEffect(() => {
    void run(async () => {
      await loadCustomers();
      await loadInternalItems();
    });
  }, [run, loadCustomers, loadInternalItems]);

  const selectCustomer = (id: string) => {
    setSelected(id);
    setRowFilter('');
    void run(() => loadMapper(id));
  };

  const noWorkOrder =
    mapper?.customer.priceListState === 'no_work_order' && mapper.options.length === 0;
  const unmappedCount = mapper ? mapper.rows.filter((r) => !r.mapping).length : 0;

  // Filter the Facil-IT rows by Facil-IT name/code or by the Corrigo item mapped
  // to them — so search finds a line either way.
  const filterRows = (rows: MapperRow[]): MapperRow[] => {
    const q = rowFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const i = r.internal;
      return (
        i.name.toLowerCase().includes(q) ||
        i.code.toLowerCase().includes(q) ||
        (r.option?.name ?? '').toLowerCase().includes(q) ||
        (r.option?.description ?? '').toLowerCase().includes(q)
      );
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/"
        icon={<IconMapping className="h-5 w-5" />}
        title="Corrigo Price List Mapping"
        subtitle="For each of our Facil-IT line items, pick the customer's matching Corrigo price-list item. Search the dropdown to find the best match."
      />

      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      <Card>
        <CardHeader
          title="Customer"
          subtitle="Pick a client, then map our Facil-IT line items to their Corrigo price-list items."
          action={busy ? <Spinner /> : undefined}
        />
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Combobox
              className="min-w-64"
              value={selected}
              onSelect={selectCustomer}
              placeholder="Type to find a customer…"
              emptyLabel="No matching customers"
              options={customers.map((c) => ({
                value: c.customerId,
                label: `${c.name} (${c.customerId})${
                  c.priceListState === 'no_work_order' ? ' — no work order' : ''
                }`,
                keywords: `${c.name} ${c.customerId}`,
              }))}
            />
            <Button
              variant="secondary"
              onClick={() =>
                run(async () => {
                  await adminApi.refreshCustomers();
                  await loadCustomers();
                }, 'Customers refreshed from Corrigo')
              }
              disabled={busy}
            >
              Refresh customers from Corrigo
            </Button>
          </div>

          <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
            <Field label="Add customer — id">
              <Input value={newCustId} onChange={(e) => setNewCustId(e.target.value)} placeholder="12345" />
            </Field>
            <Field label="name">
              <Input value={newCustName} onChange={(e) => setNewCustName(e.target.value)} placeholder="ACME Corp" />
            </Field>
            <Button
              variant="secondary"
              disabled={busy || !newCustId.trim()}
              onClick={() =>
                run(async () => {
                  await adminApi.addCustomer(newCustId.trim(), newCustName.trim());
                  setNewCustId('');
                  setNewCustName('');
                  await loadCustomers();
                }, 'Customer added')
              }
            >
              Add
            </Button>
          </div>
        </CardBody>
      </Card>

      {mapper && (
        <>
          <Card>
            <CardHeader
              title={`Facil-IT → Corrigo — ${mapper.customer.name}`}
              subtitle={
                mapper.priceListFetchedAt
                  ? `${mapper.options.length} Corrigo items available · pulled ${new Date(
                      mapper.priceListFetchedAt,
                    ).toLocaleString()}`
                  : 'No Corrigo price list cached yet — refresh from Corrigo to populate the dropdowns.'
              }
              action={
                <div className="flex items-center gap-2">
                  <Input
                    value={refreshWoId}
                    onChange={(e) => setRefreshWoId(e.target.value)}
                    placeholder="WO id (optional)"
                    inputMode="numeric"
                    className="w-40"
                  />
                  <Button
                    variant="secondary"
                    disabled={busy || (refreshWoId.trim() !== '' && !/^\d+$/.test(refreshWoId.trim()))}
                    onClick={() =>
                      run(async () => {
                        const woId = refreshWoId.trim() ? Number(refreshWoId.trim()) : undefined;
                        const res = await adminApi.refreshPriceList(selected, woId);
                        await loadMapper(selected);
                        setNotice(
                          res.noWorkOrder
                            ? 'This customer has no work order yet, so the price list cannot be pulled. Try again once they have one.'
                            : `Merged ${res.count} price-list items`,
                        );
                      })
                    }
                  >
                    Refresh price list
                  </Button>
                </div>
              }
            />
            <CardBody className="space-y-4">
              {noWorkOrder && (
                <Banner tone="warn">
                  Corrigo price list not yet available — this customer has no work order, and the
                  catalog can only be pulled via a work order. Refresh again once they have one (enter
                  a specific WO id above if you have it).
                </Banner>
              )}

              {!noWorkOrder && mapper.options.length === 0 && (
                <Banner tone="warn">
                  No Corrigo price-list items cached for this customer yet. Refresh the price list above
                  to populate the dropdowns.
                </Banner>
              )}

              {mapper.rows.length === 0 ? (
                <p className="text-sm text-slate-400">
                  No active Facil-IT line items. Add some in the “Facil-IT line items” section below.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={unmappedCount > 0 ? 'amber' : 'green'}>
                      {unmappedCount} unmapped Facil-IT item{unmappedCount === 1 ? '' : 's'}
                    </Badge>
                    <span className="text-xs text-slate-400">
                      Each Facil-IT item maps to one Corrigo item; several Facil-IT items may share
                      the same Corrigo item.
                    </span>
                    <div className="ml-auto flex gap-2">
                      <Button variant="ghost" onClick={() => run(() => adminApi.exportCsv(selected))}>
                        Download CSV
                      </Button>
                      <Button variant="ghost" onClick={() => run(() => adminApi.exportJson(selected))}>
                        Export JSON
                      </Button>
                    </div>
                  </div>

                  <div className="relative max-w-sm">
                    <Input
                      value={rowFilter}
                      onChange={(e) => setRowFilter(e.target.value)}
                      placeholder="Search Facil-IT items (name, code, mapped Corrigo item)…"
                    />
                    {rowFilter && (
                      <button
                        type="button"
                        onClick={() => setRowFilter('')}
                        title="Clear search"
                        className="absolute inset-y-0 right-2 my-auto h-5 text-slate-400 hover:text-slate-600"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {(() => {
                    const visibleRows = filterRows(mapper.rows);
                    return (
                      <>
                        {rowFilter && (
                          <p className="text-xs text-slate-400">
                            {visibleRows.length} of {mapper.rows.length} item
                            {mapper.rows.length === 1 ? '' : 's'} match “{rowFilter}”.
                          </p>
                        )}
                        <div className="overflow-x-auto rounded-lg border border-slate-200">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-400">
                              <tr>
                                <th className="px-3 py-2">Facil-IT line item</th>
                                <th className="px-3 py-2">→ Corrigo price-list item</th>
                                <th className="px-3 py-2 text-right">Rate</th>
                                <th className="px-3 py-2 text-center">Locked</th>
                                <th className="px-3 py-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleRows.length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                                    No Facil-IT items match “{rowFilter}”.
                                  </td>
                                </tr>
                              ) : (
                                visibleRows.map((row) => (
                                  <FacilitRow
                                    key={row.internal.id}
                                    row={row}
                                    options={mapper.options}
                                    busy={busy}
                                    onSelect={(priceListItemId) =>
                                      run(async () => {
                                        await adminApi.saveMapping(selected, {
                                          internalLineItemId: row.internal.id,
                                          priceListItemId,
                                        });
                                        await loadMapper(selected);
                                      }, 'Mapping saved')
                                    }
                                    onOverride={(overrideRate) =>
                                      run(async () => {
                                        if (!row.mapping) return;
                                        await adminApi.saveMapping(selected, {
                                          internalLineItemId: row.internal.id,
                                          priceListItemId: row.mapping.priceListItemId,
                                          overrideRate,
                                        });
                                        await loadMapper(selected);
                                      }, 'Rate updated')
                                    }
                                    onClear={() =>
                                      run(async () => {
                                        await adminApi.deleteMapping(selected, row.internal.id);
                                        await loadMapper(selected);
                                      }, 'Mapping removed')
                                    }
                                  />
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </CardBody>
          </Card>

          <ImportPanel
            customerId={selected}
            busy={busy}
            onApplied={() => run(() => loadMapper(selected), 'Mappings imported')}
            setError={setError}
          />
        </>
      )}

      <Card>
        <CardHeader
          title="Facil-IT line items"
          subtitle="Our master list. Edit a name, or deactivate an item to hide it from mapping without losing history. Taxable / Not-Taxable variants are distinct items; the proposal's tax checkbox picks the variant."
        />
        <CardBody className="space-y-4">
          {internalItems.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-400">
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Code</th>
                    <th className="px-3 py-2">Taxable</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {internalItems.map((item) => (
                    <InternalItemRow
                      key={item.id}
                      item={item}
                      busy={busy}
                      onSave={(name) =>
                        run(async () => {
                          await adminApi.updateInternalItem(item.id, { name });
                          await loadInternalItems();
                          if (selected) await loadMapper(selected);
                        }, 'Facil-IT item updated')
                      }
                      onToggleActive={() =>
                        run(
                          async () => {
                            await adminApi.updateInternalItem(item.id, { active: !item.active });
                            await loadInternalItems();
                            if (selected) await loadMapper(selected);
                          },
                          item.active ? 'Facil-IT item deactivated' : 'Facil-IT item activated',
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
            <Field label="Code">
              <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="LABOR-T" />
            </Field>
            <Field label="Name">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Labor - Taxable" />
            </Field>
            <Field label="Taxable">
              <Select value={newTaxable} onChange={(e) => setNewTaxable(e.target.value as typeof newTaxable)}>
                <option value="null">Tax-agnostic</option>
                <option value="true">Taxable</option>
                <option value="false">Not Taxable</option>
              </Select>
            </Field>
            <Button
              variant="secondary"
              disabled={busy || !newCode.trim() || !newName.trim()}
              onClick={() =>
                run(async () => {
                  await adminApi.createInternalItem({
                    code: newCode.trim(),
                    name: newName.trim(),
                    taxable: newTaxable === 'null' ? null : newTaxable === 'true',
                  });
                  setNewCode('');
                  setNewName('');
                  setNewTaxable('null');
                  await loadInternalItems();
                  if (selected) await loadMapper(selected);
                }, 'Facil-IT item added')
              }
            >
              Add Facil-IT item
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/** One row of the Facil-IT master list: inline-editable name + activate/deactivate. */
function InternalItemRow({
  item,
  busy,
  onSave,
  onToggleActive,
}: {
  item: InternalLineItem;
  busy: boolean;
  onSave: (name: string) => void;
  onToggleActive: () => void;
}) {
  const [name, setName] = useState(item.name);
  const dirty = name.trim() !== '' && name.trim() !== item.name;

  return (
    <tr className={`border-t border-slate-100 align-middle ${item.active ? '' : 'opacity-60'}`}>
      <td className="px-3 py-2">
        <Input value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
      </td>
      <td className="px-3 py-2 font-mono text-xs text-slate-500">{item.code}</td>
      <td className="px-3 py-2 text-slate-500">
        {item.taxable === true ? 'Taxable' : item.taxable === false ? 'Not Taxable' : 'Tax-agnostic'}
      </td>
      <td className="px-3 py-2">
        <Badge tone={item.active ? 'green' : 'slate'}>{item.active ? 'Active' : 'Inactive'}</Badge>
      </td>
      <td className="px-3 py-2">
        <div className="flex justify-end gap-2">
          <Button variant="secondary" disabled={busy || !dirty} onClick={() => onSave(name.trim())}>
            Save
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onToggleActive}>
            {item.active ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function taxableLabel(t: boolean | null): string {
  return t === true ? ' (Taxable)' : t === false ? ' (Not Taxable)' : '';
}

/** Build the dropdown label for a Corrigo option: name + rate + tax code. */
function optionLabel(o: PriceListOption): string {
  const parts = [o.name];
  if (o.defaultRate != null) parts.push(formatMoney(o.defaultRate));
  if (o.taxCode) parts.push(o.taxCode);
  return parts.join(' · ');
}

/**
 * One Facil-IT line item (left) and the Corrigo price-list item it maps to
 * (right): a searchable dropdown over the customer's price list, plus the
 * effective rate (editable override when the Corrigo rate isn't locked).
 */
function FacilitRow({
  row,
  options,
  busy,
  onSelect,
  onOverride,
  onClear,
}: {
  row: MapperRow;
  options: PriceListOption[];
  busy: boolean;
  onSelect: (priceListItemId: string) => void;
  onOverride: (overrideRate: number | null) => void;
  onClear: () => void;
}) {
  const mapped = row.mapping != null && row.option != null;
  const [override, setOverride] = useState<string>(
    row.mapping?.overrideRate != null ? String(row.mapping.overrideRate) : '',
  );

  // Keep the override input in sync after a reload (e.g. a different rate saved).
  useEffect(() => {
    setOverride(row.mapping?.overrideRate != null ? String(row.mapping.overrideRate) : '');
  }, [row.mapping?.overrideRate, row.mapping?.priceListItemId]);

  return (
    <tr className={`border-t border-slate-100 align-top ${mapped ? '' : 'bg-amber-50/40'}`}>
      <td className="px-3 py-2">
        <div className="font-medium text-slate-800">
          {row.internal.name}
          {taxableLabel(row.internal.taxable)}
        </div>
        <div className="font-mono text-xs text-slate-400">{row.internal.code}</div>
      </td>
      <td className="px-3 py-2">
        <Combobox
          className="min-w-72"
          value={row.mapping?.priceListItemId ?? ''}
          disabled={busy || options.length === 0}
          placeholder={
            options.length === 0
              ? 'Refresh the price list first…'
              : '— type to find a Corrigo item —'
          }
          emptyLabel="No matching Corrigo items"
          onSelect={onSelect}
          options={options.map((o) => ({
            value: o.priceListItemId,
            label: optionLabel(o),
            keywords: `${o.name} ${o.description ?? ''} ${o.taxCode ?? ''}`,
          }))}
        />
        {row.option == null && row.mapping != null && (
          <p className="mt-1 text-xs text-amber-600">
            Mapped item is no longer in the customer's price list — pick another.
          </p>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {!mapped ? (
          <span className="text-slate-300">—</span>
        ) : row.rateLocked ? (
          <span className="text-xs text-slate-600">{formatMoney(row.effectiveRate)} 🔒</span>
        ) : (
          <input
            type="number"
            step="0.01"
            value={override}
            disabled={busy}
            placeholder={row.option?.defaultRate != null ? String(row.option.defaultRate) : 'rate'}
            onChange={(e) => setOverride(e.target.value)}
            onBlur={() => {
              const next = override.trim() === '' ? null : Number(override);
              if (next !== (row.mapping?.overrideRate ?? null)) onOverride(next);
            }}
            className="w-24 rounded border border-slate-300 px-2 py-0.5 text-right text-xs"
          />
        )}
      </td>
      <td className="px-3 py-2 text-center">{mapped && row.rateLocked ? '🔒' : ''}</td>
      <td className="px-3 py-2 text-right">
        {mapped && (
          <button
            type="button"
            disabled={busy}
            onClick={onClear}
            title="Remove this mapping"
            className="px-1 text-slate-400 hover:text-red-600 disabled:opacity-50"
          >
            ×
          </button>
        )}
      </td>
    </tr>
  );
}

/** Bulk CSV upload: validate (preview) then apply. */
function ImportPanel({
  customerId,
  busy,
  onApplied,
  setError,
}: {
  customerId: string;
  busy: boolean;
  onApplied: () => void;
  setError: (msg: string | null) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<MappingImportResult | null>(null);
  const [working, setWorking] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const doPreview = async (f: File) => {
    setWorking(true);
    setError(null);
    try {
      setPreview(await adminApi.importCsv(customerId, f, { apply: false }));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not read the CSV');
    } finally {
      setWorking(false);
    }
  };

  const doApply = async (addMissing: boolean) => {
    if (!file) return;
    setWorking(true);
    setError(null);
    try {
      const res = await adminApi.importCsv(customerId, file, { apply: true, addMissing });
      if (res.applied) {
        reset();
        onApplied();
      } else {
        setPreview(res); // still has errors
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Import failed');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Bulk import (CSV)"
        subtitle="Upload a mappings CSV (same columns as Download). Every row is validated; nothing is applied unless all rows are valid."
      />
      <CardBody className="space-y-3">
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setFile(f);
                void doPreview(f);
              }
            }}
          />
          <Button variant="secondary" disabled={busy || working} onClick={() => fileRef.current?.click()}>
            {working ? <Spinner /> : 'Choose CSV'}
          </Button>
          {file && <span className="text-sm text-slate-500">{file.name}</span>}
          {preview && (
            <Button variant="ghost" onClick={reset}>
              Clear
            </Button>
          )}
        </div>

        {preview && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone={preview.errorCount === 0 ? 'green' : 'red'}>
                {preview.okCount} ok · {preview.errorCount} error{preview.errorCount === 1 ? '' : 's'}
              </Badge>
              {preview.missingFacilit.length > 0 && (
                <Badge tone="amber">{preview.missingFacilit.length} missing Facil-IT item(s)</Badge>
              )}
            </div>

            {preview.results.some((r) => r.status === 'error') && (
              <div className="max-h-48 overflow-y-auto rounded border border-red-200 bg-red-50 p-2 text-xs">
                {preview.results
                  .filter((r) => r.status === 'error')
                  .map((r) => (
                    <div key={`${r.row}-${r.priceListItemId}`} className="text-red-700">
                      Row {r.row} ({r.priceListItemId}): {r.message}
                    </div>
                  ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button disabled={working || preview.errorCount > 0} onClick={() => doApply(false)}>
                {working ? <Spinner /> : 'Apply mappings'}
              </Button>
              {preview.missingFacilit.length > 0 && (
                <Button
                  variant="secondary"
                  disabled={working}
                  onClick={() => doApply(true)}
                  title="Create the missing Facil-IT items (tax-agnostic), then apply"
                >
                  Add missing Facil-IT items &amp; apply
                </Button>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
